<template>
  <div
    :style="appHeaderStyle"
    class="app-content"
  >
    <app-header
      v-if="selectedPageId !== 'success-page'"
      :page="page"
      :user-display-name="currentUser.email"
      :user="currentUser"
      :workspace-title="workspace.name"
      :workspace-icon="workspace.icon"
      @navigate="changePage"
      @logout="logout"
    />
    <c-alert
      v-if="subscriptionPastDue"
      icon="warning"
      color="warning"
      title="Your subscription payment is past due. Please update your payment details to continue uninterrupted access."
      :style="{margin:'2rem 2rem 0 2rem'}"
      :action="{ title: 'Update Payment Details', target: '_blank', href: `${websiteUrl}/app/plans`}"
    />
    <c-container
      v-if="!settingsService.loading.value && apiKeyNeeded && ['templates', 'workspaces'].includes(selectedPageId)"
      padding-size="small"
      @click="changePage('settings')"
    >
      <div
        :style="{ display: 'flex', alignItems: 'center', padding: '.8rem 1.4rem', borderRadius: '4px' }"
        class="_key-hint"
      >
        <div :style="{ display: 'flex', marginRight: '1rem' }">
          <div
            class="_icon"
            :style="{ marginRight: '1rem', marginTop: '.4rem' }"
          >
            <c-icon
              type="warning"
              :color="$colors.yellow.darken2"
            />
          </div>
          Add your Open AI Api Key to use our AI features.
        </div>
        <div
          :style="{ marginLeft: 'auto' }"
        >
          <c-button
            outlined
          >
            Go to settings
          </c-button>
        </div>
      </div>
    </c-container>
    <see-workspaces
      v-if="selectedPageId === 'workspaces'"
      @see-templates="seeTemplates"
    />
    <see-templates
      v-if="selectedPageId === 'templates'"
      @use-template="useTemplate"
      @add-template="changePage('databases')"
      @edit-template="editTemplate"
      @without-template="withoutTemplate"
    />
    <see-databases
      v-if="selectedPageId === 'databases'"
      :workspace="workspace"
      :create-without-template="createWithoutTemplate"
      @edit-template="editTemplate"
      @use-template="useTemplate"
    />
    <edit-template
      v-if="selectedPageId === 'editTemplate'"
      @leave="changePage('templates')"
    />
    <use-template
      v-if="selectedPageId === 'useTemplate'"
      :selection="selection"
      :selection-text="selectionText"
      :body-text="bodyText"
      :body-html="bodyHtml"
      :thumbnail="thumbnail"
      :page-url="pageUrl"
      :page-title="pageTitle"
      :cookie="cookie"
      :local-storage="localStorage"
      :parent-host="host"
      @leave="changePage('templates')"
      @go-to-success-page="goToSuccessPage"
    />
    <settings-page v-if="selectedPageId === 'settings'" />
    <history-page v-if="selectedPageId === 'history'" />
    <success-page
      v-if="selectedPageId === 'success-page'"
      :response="createResponse"
      @leave="changePage('templates')"
    />
  </div>
</template>

<script>
import AppHeader from 'shared/components/AppHeader.vue';
import CAlert from 'shared/components/CAlert.vue';
import CButton from 'shared/components/CButton.vue';
import CContainer from 'shared/components/CContainer.vue';
import CIcon from 'shared/components/CIcon.vue';
import EditTemplate from '@/components/EditTemplate.vue';
import HistoryPage from '@/components/HistoryPage.vue';
import SeeDatabases from '@/components/SeeDatabases.vue';
import SeeTemplates from '@/components/SeeTemplates.vue';
import SeeWorkspaces from '@/components/SeeWorkspaces.vue';
import SettingsPage from '@/components/SettingsPage.vue';
import SuccessPage from '@/components/SuccessPage.vue';
import UseTemplate from '@/components/UseTemplate.vue';
import useFeatureAccess from 'shared/services/feature-access';
import useHistory from 'shared/services/history';
import useNotionDatabase from 'shared/services/notion-database';
import useSettings from 'shared/services/settings';
import useSubscription from 'shared/services/subscription';
import useTemplate from 'shared/services/template';
import {
  BETA_PHASE_MANAGER,
  FIREBASE_APP, HISTORY_SERVICE, NOTION_CLIENT, NOTION_DATABASE_SERVICE,
  SETTINGS_SERVICE,
  SUBSCRIPTION_MANAGER,
  TEMPLATE_SERVICE,
  USER_SERVICE, WORKSPACE_SERVICE, featureFlags,
} from 'shared/constants';
import { httpsCallable, signOut } from 'shared/services/firebase';
import { inject, provide } from 'vue';

export default {
  name: 'AppContent',
  setup() {
    const firebaseApp = inject(FIREBASE_APP);
    const subscriptionManager = inject(SUBSCRIPTION_MANAGER);
    const userService = inject(USER_SERVICE);
    const workspaceService = inject(WORKSPACE_SERVICE);
    const notionClient = inject(NOTION_CLIENT);
    const updateDatabases = httpsCallable(firebaseApp.functions, 'updateDatabases');
    const notionDatabaseService = useNotionDatabase(notionClient);

    const historySvc = useHistory(firebaseApp.db, userService.currentUser, subscriptionManager);
    provide(HISTORY_SERVICE, historySvc);
    historySvc.init();

    provide(NOTION_DATABASE_SERVICE, notionDatabaseService);
    const templateService = useTemplate(firebaseApp.db, userService.currentUser, subscriptionManager);
    provide(TEMPLATE_SERVICE, templateService);

    const settingsService = useSettings(firebaseApp.db, userService.currentUser, subscriptionManager);
    provide(SETTINGS_SERVICE, settingsService);
    settingsService.init();

    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const { currentPlan, subscriptionPastDue } = useSubscription(userService.currentUser, betaPhaseManager);
    const { hasFeatureAccess } = useFeatureAccess(currentPlan, subscriptionPastDue, betaPhaseManager);

    return {
      hasFeatureAccess,
      currentUser: userService.currentUser,
      workspaceService,
      workspace: workspaceService.selectedWorkspace,
      templateService,
      notionDatabaseService,
      workspacesLoading: workspaceService.workspacesLoading,
      updateDatabases,
      settingsService,
      subscriptionPastDue,
      auth: firebaseApp.auth,
    };
  },
  components: { HistoryPage, SettingsPage, SeeWorkspaces, SeeTemplates, SeeDatabases, EditTemplate, UseTemplate, AppHeader, SuccessPage, CIcon, CContainer, CButton, CAlert },
  data() {
    return {
      selectedPageId: 'workspaces',
      createResponse: null,
      selection: '',
      selectionText: '',
      bodyText: '',
      bodyHtml: '',
      thumbnail: '',
      pageUrl: '',
      pageTitle: '',
      cookie: '',
      localStorage: '',
      host: '',
      createWithoutTemplate: false,
      history: [],
    };
  },
  emits: ['navigate'],
  computed: {
    websiteUrl() {
      return import.meta.env.VITE_WEBSITE_URL;
    },
    apiKeyNeeded() {
      return this.hasFeatureAccess(featureFlags.aiProperties) && this.settingsService.settings.value.openAIKey === undefined;
    },
    appHeaderStyle() {
      const style = { minHeight: '100vh' };
      if (['editTemplate', 'useTemplate', 'databases'].includes(this.selectedPageId)) {
        return { ...style, backgroundColor: 'white' };
      }

      return style;
    },
    page() {
      switch (this.selectedPageId) {
        case 'workspaces':
          return {
            left: { component: 'logo' },
            center: { component: '' },
            right: { component: 'user' },
          };
        case 'templates':
          return {
            left: {
              component: 'button',
              icon: 'left',
              onclick: () => {
                this.changePage('workspaces');
              },
            },
            center: { component: 'workspace' },
            right: { component: 'user' },
          };
        case 'databases':
          return {
            left: {
              component: 'button',
              icon: 'left-arrow-long',
              onclick: () => {
                this.changePage('templates');
              },
            },
            center: { component: 'text', text: 'Select a database' },
            right: { component: '' },
          };
        case 'useTemplate':
          return {
            left: {
              component: 'button',
              icon: 'left-arrow-long',
              onclick: () => {
                this.changePage('templates');
              },
            },
            center: { component: 'text', text: `Add to ${this.templateService.selectedTemplate.value.database.name}` },
            right: { component: '' },
          };
        case 'editTemplate':
          return {
            left: {
              component: 'button',
              icon: 'left-arrow-long',
              onclick: () => {
                this.changePage('templates');
              },
            },
            center: { component: 'text', text: 'Edit template' },
            right: { component: '' },
          };
        case 'settings':
          return {
            left: {
              component: 'button',
              icon: 'left-arrow-long',
              onclick: () => {
                this.goBack();
              },
            },
            center: { component: 'text', text: 'Settings' },
            right: { component: 'user' },
          };
        case 'history':
          return {
            left: {
              component: 'button',
              icon: 'left-arrow-long',
              onclick: () => {
                this.goBack();
              },
            },
            center: { component: 'text', text: 'History' },
            right: { component: 'user' },
          };
        default:
          return {};
      }
    },
  },
  methods: {
    goBack() {
      if (this.history.length <= 1) {
        return;
      }
      if (['history', 'settings'].includes(this.history[this.history.length - 2])) {
        if (this.currentUser.lastSelectedWorkspace !== undefined) {
          this.changePage('templates');
          return;
        }
        this.changePage('workspaces');
      }
      this.changePage(this.history[this.history.length - 2]);
    },
    withoutTemplate() {
      this.selectedPageId = 'databases';
      this.createWithoutTemplate = true;
    },
    goToSuccessPage(value) {
      this.selection = '';
      this.bodyText = '';
      this.bodyHtml = '';
      this.thumbnail = '';
      this.createResponse = value;
      this.selectedPageId = 'success-page';
    },
    changePage(value) {
      if (this.history.length > 20) {
        this.history.shift();
      }
      this.history.push(value);
      this.selectedPageId = value;
    },
    async logout() {
      await signOut(this.auth);
      this.$subscriptionManager.unsubscribeAll();
    },
    seeTemplates(workspaceId) {
      if (this.workspaceService.getSingle(workspaceId) === undefined) {
        this.workspaceService.clearLastSelectedWorkspace();
        return;
      }
      this.updateDatabases({ workspaceID: workspaceId });
      this.workspaceService.selectWorkspace(workspaceId);
      this.notionDatabaseService.init(this.workspace);
      this.templateService.init(this.workspace);
      this.changePage('templates');
    },
    resetCreateWithoutTemplate() {
      this.createWithoutTemplate = false;
    },
    useTemplate(templateId) {
      this.resetCreateWithoutTemplate();
      this.templateService.selectTemplate(templateId);
      this.changePage('useTemplate');
    },
    editTemplate(templateId) {
      this.templateService.selectTemplate(templateId);
      this.changePage('editTemplate');
    },
    handleMessage(event) {
      if (event.data.id === 'NOTIX_READY_RESPONSE') {
        this.selection = event.data.payload.selection;
        this.selectionText = event.data.payload.selectionText;
        this.bodyText = event.data.payload.innerText;
        this.bodyHtml = event.data.payload.innerHTML;
        this.thumbnail = event.data.payload.thumbnail;
        this.pageUrl = event.data.payload.url;
        this.pageTitle = event.data.payload.title;
        this.cookie = event.data.payload.cookie;
        this.localStorage = event.data.payload.localStorage;
        this.host = event.data.payload.host;
      }
    },
    goToSelectedWorkspace() {
      if (this.currentUser.lastSelectedWorkspace !== undefined) {
        this.seeTemplates(this.currentUser.lastSelectedWorkspace);
        return;
      }

      this.history.push('workspaces');
    },
  },
  created() {
    window.addEventListener('message', this.handleMessage);
    window.parent.postMessage({ msg: 'NOTIX_READY' }, '*');
    this.goToSelectedWorkspace();
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleMessage);
  },
};
</script>

<style scoped lang="scss" type="text/scss">
.app-content {
  ._key-hint {
    background-color: map-get($yellow, 'lighten-4');
    border: 1px solid map-get($yellow, 'base');
    cursor: pointer;

    &:hover {
      background-color: map-get($yellow, 'lighten-3');
    }
  }
}
</style>
